import { useMutation, UseMutationResult } from 'react-query';

import { useAuth } from 'src/hooks';

import { downloadAsCsv } from 'src/utils/urls';
import { pageLength } from 'src/utils/appConstants';

import { FilterOptionsType } from 'src/features/Users/types/usersListType';
import { createPayload } from '../utils/reqDataCsv';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type ApiResponse<TData = unknown> = {
  Data?: TData;
  Errors?: ApiErrorItem[];
  ErrorId?: string;
  IsSuccess: boolean;
  StatusCode: number;
};

type TResult = ApiResponse | 'Success';

type TError = Error;

type TArguments = {
  token: string;
  tokenType: string;
  requestData: any;
  recordsLoaded: number;
};

type TVariables = {
  filterOptions: FilterOptionsType;
  recordsLoaded?: number;
};

async function postDownloadCsv({
  token,
  tokenType,
  requestData,
  recordsLoaded,
}: TArguments): Promise<TResult> {
  const lengthToRequest = recordsLoaded > 0 ? recordsLoaded : pageLength;

  const updatedRequestData = {
    ...requestData,
    start: 0,
    length: lengthToRequest,
  };

  const response = await fetch(downloadAsCsv, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updatedRequestData),
  });

  if (response.status === 204) {
    return 'Success';
  }

  let result: ApiResponse | null = null;

  try {
    const text = await response.text();
    result = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!result) {
    throw new Error('Invalid server response');
  }

  return result;
}

function useDownloadCsv(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  unknown
> {
  const { token, tokenType } = useAuth();

  return useMutation(async (params: TVariables) => {
    const requestData = createPayload(params.filterOptions);

    const result = await postDownloadCsv({
      token,
      tokenType,
      requestData,
      recordsLoaded: params.recordsLoaded ?? 0,
    });

    if (result === 'Success') {
      return result;
    }

    if (!result.IsSuccess) {
      const errorMessage =
        result.Errors?.map(err => err.Message).join(', ') ||
        'Failed to download CSV';

      throw new Error(errorMessage);
    }

    return result;
  });
}

export { useDownloadCsv };
